import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { AdminPasswordConfirmDialog } from './AdminPasswordConfirmDialog';
import { getAllSubscriptionTiers, updateSubscriptionTier, type SubscriptionTier, type SubscriptionTierUpdate } from '@/api/pricing';
import { Crown, Edit, Save, X, DollarSign, Coins, Gift } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface EditingTier extends SubscriptionTierUpdate {
  tierId: string;
}

interface TierCardProps {
  tier: SubscriptionTier;
  editingTier: EditingTier | null;
  setEditingTier: (tier: EditingTier | null) => void;
  handleEdit: (tier: SubscriptionTier) => void;
  handleCancelEdit: () => void;
  handleSaveClick: () => void;
  updateMutation: any;
}

const TierCard: React.FC<TierCardProps> = ({
  tier,
  editingTier,
  setEditingTier,
  handleEdit,
  handleCancelEdit,
  handleSaveClick,
  updateMutation
}) => {
  const isEditing = editingTier?.tierId === tier.id;
  const isPro = tier.tier_level === 'pro';
  const isFree = tier.tier_level === 'free';

  return (
    <Card className={`relative ${isPro ? 'border-primary shadow-lg' : ''}`}>
      {isPro && (
        <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold rounded-bl-lg rounded-tr-lg">
          POPULAR
        </div>
      )}
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {isPro && <Crown className="h-5 w-5 text-primary" />}
            {isEditing ? (
              <Input
                value={editingTier.tier_name || ''}
                onChange={(e) => setEditingTier({ ...editingTier, tier_name: e.target.value })}
                className="w-full"
              />
            ) : (
              tier.tier_name
            )}
          </CardTitle>
          {!isEditing && (
            <Button size="sm" variant="ghost" onClick={() => handleEdit(tier)}>
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Badge variant={tier.is_active ? 'default' : 'secondary'} className="w-fit">
          {tier.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Monthly Price */}
          <div>
            <Label className="text-sm text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Monthly Price
            </Label>
            {isEditing ? (
              <Input
                type="number"
                step="0.01"
                value={editingTier.monthly_price || ''}
                onChange={(e) => setEditingTier({ ...editingTier, monthly_price: parseFloat(e.target.value) })}
                className="mt-1"
              />
            ) : (
              <p className="text-2xl font-bold">${tier.monthly_price.toFixed(2)}/mo</p>
            )}
          </div>

          {/* Monthly Credits */}
          <div>
            <Label className="text-sm text-muted-foreground flex items-center gap-2">
              <Coins className="h-4 w-4" />
              Monthly Credits Included
            </Label>
            {isEditing ? (
              <Input
                type="number"
                value={editingTier.monthly_credits_included ?? ''}
                onChange={(e) => setEditingTier({ ...editingTier, monthly_credits_included: parseInt(e.target.value) })}
                className="mt-1"
              />
            ) : (
              <p className="text-lg font-semibold">{tier.monthly_credits_included} credits</p>
            )}
          </div>

          {/* Signup Bonus */}
          <div>
            <Label className="text-sm text-muted-foreground flex items-center gap-2">
              <Gift className="h-4 w-4" />
              Signup Bonus Credits
            </Label>
            {isEditing ? (
              <Input
                type="number"
                value={editingTier.signup_bonus_credits ?? ''}
                onChange={(e) => setEditingTier({ ...editingTier, signup_bonus_credits: parseInt(e.target.value) })}
                className="mt-1"
              />
            ) : (
              <p className="text-lg font-semibold text-green-600">{tier.signup_bonus_credits} credits</p>
            )}
          </div>

          {/* Overage Multiplier */}
          <div>
            <Label className="text-sm text-muted-foreground">Overage Rate Multiplier</Label>
            {isEditing ? (
              <Input
                type="number"
                step="0.01"
                value={editingTier.overage_rate_multiplier ?? ''}
                onChange={(e) => setEditingTier({ ...editingTier, overage_rate_multiplier: parseFloat(e.target.value) })}
                className="mt-1"
              />
            ) : (
              <p className="text-sm">
                {tier.overage_rate_multiplier}x
                {tier.overage_rate_multiplier < 1 && (
                  <span className="text-green-600 ml-2">
                    ({Math.round((1 - tier.overage_rate_multiplier) * 100)}% discount)
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Features */}
          <div>
            <Label className="text-sm text-muted-foreground">Features</Label>
            <div className="mt-2 space-y-1">
              {tier.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <span className="text-green-500">✓</span>
                  <span>{feature.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stripe Price ID */}
          {isEditing && (
            <div>
              <Label className="text-sm text-muted-foreground">Stripe Price ID</Label>
              <Input
                value={editingTier.stripe_price_id || ''}
                onChange={(e) => setEditingTier({ ...editingTier, stripe_price_id: e.target.value })}
                placeholder="price_xxxxx"
                className="mt-1"
              />
            </div>
          )}

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleSaveClick}
                disabled={updateMutation.isPending}
                className="flex-1"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const SubscriptionTierManager: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingTier, setEditingTier] = useState<EditingTier | null>(null);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{ tierId: string; update: SubscriptionTierUpdate } | null>(null);

  // Fetch subscription tiers
  const { data: tiers, isLoading, error } = useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: getAllSubscriptionTiers,
  });

  // Update tier mutation
  const updateMutation = useMutation({
    mutationFn: ({ tierId, update, password }: { tierId: string; update: SubscriptionTierUpdate; password: string }) =>
      updateSubscriptionTier(tierId, update, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-tiers'] });
      toast({
        title: "Tier Updated",
        description: "Subscription tier has been updated successfully.",
      });
      setEditingTier(null);
      setPendingUpdate(null);
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update subscription tier.",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (tier: SubscriptionTier) => {
    setEditingTier({
      tierId: tier.id,
      tier_name: tier.tier_name,
      monthly_price: tier.monthly_price,
      monthly_credits_included: tier.monthly_credits_included,
      signup_bonus_credits: tier.signup_bonus_credits,
      overage_rate_multiplier: tier.overage_rate_multiplier,
      features: tier.features,
      stripe_price_id: tier.stripe_price_id,
      is_active: tier.is_active,
      display_order: tier.display_order,
    });
  };

  const handleCancelEdit = () => {
    setEditingTier(null);
  };

  const handleSaveClick = () => {
    if (!editingTier) return;

    const { tierId, ...update } = editingTier;
    setPendingUpdate({ tierId, update });
    setShowPasswordDialog(true);
  };

  const handlePasswordConfirm = async (password: string) => {
    if (!pendingUpdate) return;

    await updateMutation.mutateAsync({
      tierId: pendingUpdate.tierId,
      update: pendingUpdate.update,
      password,
    });
  };

  const formatUserType = (userType: string) => {
    return userType.charAt(0).toUpperCase() + userType.slice(1);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">
            Error loading subscription tiers: {(error as Error).message}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group tiers by user type
  const individualTiers = tiers?.filter(t => t.user_type === 'individual').sort((a, b) => a.display_order - b.display_order) || [];
  const practitionerTiers = tiers?.filter(t => t.user_type === 'practitioner').sort((a, b) => a.display_order - b.display_order) || [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Subscription Tier Management</CardTitle>
          <CardDescription>
            Configure subscription plans, pricing, and included credits for different user types
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Individual Tiers */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4">Individual User Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {individualTiers.map((tier) => (
                <TierCard
                  key={tier.id}
                  tier={tier}
                  editingTier={editingTier}
                  setEditingTier={setEditingTier}
                  handleEdit={handleEdit}
                  handleCancelEdit={handleCancelEdit}
                  handleSaveClick={handleSaveClick}
                  updateMutation={updateMutation}
                />
              ))}
            </div>
          </div>

          {/* Practitioner Tiers */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Practitioner Tiers</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {practitionerTiers.map((tier) => (
                <TierCard
                  key={tier.id}
                  tier={tier}
                  editingTier={editingTier}
                  setEditingTier={setEditingTier}
                  handleEdit={handleEdit}
                  handleCancelEdit={handleCancelEdit}
                  handleSaveClick={handleSaveClick}
                  updateMutation={updateMutation}
                />
              ))}
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> Changes to subscription tiers require admin password confirmation.
              Signup bonus credits are granted once when a user creates an account. Monthly credits reset each billing period.
            </p>
          </div>
        </CardContent>
      </Card>

      <AdminPasswordConfirmDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
        onConfirm={handlePasswordConfirm}
        title="Confirm Tier Changes"
        description="You are about to modify subscription tier settings. This may affect user billing and available features. Please enter your admin password to confirm."
        actionLabel="Update Tier"
      />
    </>
  );
};
